import { ListItemIcon } from '@mui/material';
import { compact } from 'lodash';
import { useMemo } from 'react';

import {
  SelectInputOption,
  TypeaheadSelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { User01Icon } from '@/components/icons/User01Icon';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { BUSINESS_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { formatCreatedFromEntities } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { getEntityTypeFromSubtype } from '@/modules/entities/utils/getEntityTypeFromSubtype';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityKind, EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import { useRecipientData } from '../../contexts/RecipientData.context';
import { DispositionScheme } from '../../dispositiveProvisions.types';
import { PrimaryClientDeathEvent } from '../../DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { useDispositiveProvisionsTemplateSplitScreenModalContext } from '../../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.context';
import { DispositiveProvisionsTemplateRules } from '../DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.constants';
import { DispositiveProvisionsRecipientTemplateRecipientConfiguration } from '../DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.types';
import {
  DispositiveProvisionsPossibleRecipients_EntityFragment,
  DispositiveProvisionsPossibleRecipients_HouseholdFragment,
  useDispositiveProvisionsPossibleRecipientsQuery,
} from '../DispositiveProvisionsFormRecipients/graphql/DispositiveProvisionsFormRecipients.generated';
import { useDispositionScheme, useFirstClientDeathId } from './utilityHooks';

export const SURVIVING_SPOUSE_SENTINEL = 'surviving-spouse' as const;

/**
 * Auto-created personal account categories that are eligible recipients even
 * while still PRE_CREATED (before any assets are added).
 */
const PRE_CREATED_RECIPIENT_ENTITY_KINDS: EntityKind[] = [
  EntityKind.IndividualPersonalAccount,
  EntityKind.JointPersonalAccount,
  EntityKind.RetirementPersonalAccount,
];

interface FilterPossibleRecipientEntitiesInput {
  entities: DispositiveProvisionsPossibleRecipients_EntityFragment[];
  includeStages: EntityStage[];
  excludeEntities?: string[];
  customEntityFilter?: DispositiveProvisionsRecipientTemplateRecipientConfiguration['customEntityFilter'];
}

/**
 * Shared filter for "possible recipient" entities used by both the
 * Dispositive Provisions form and the Beneficiary Designations form.
 *
 * Centralizing this filter prevents BDG from silently diverging from DP
 * when (for example) new entity stages or business-entity types are
 * added to the recipient-eligibility rules.
 */
export function filterPossibleRecipientEntities({
  entities,
  includeStages,
  excludeEntities = [],
  customEntityFilter,
}: FilterPossibleRecipientEntitiesInput): DispositiveProvisionsPossibleRecipients_EntityFragment[] {
  return entities
    .filter((e) => {
      if (!includeStages.includes(e.stage)) {
        return false;
      }
      // Pre-created entities are eligible only as one of the category kinds.
      if (e.stage === EntityStage.PreCreated) {
        return PRE_CREATED_RECIPIENT_ENTITY_KINDS.includes(e.kind);
      }
      return true;
    })
    .filter((e) => {
      if (!e.subtype.__typename) {
        return false;
      }
      if (customEntityFilter) {
        return customEntityFilter(e);
      }
      // Do not include business entities in the list of possible recipients
      return !BUSINESS_ENTITY_TYPES.includes(
        getEntityTypeFromSubtype(e.subtype.__typename)
      );
    })
    .filter((e) => !excludeEntities.includes(e.id));
}

function getEntityRecipientOptions(
  input: FilterPossibleRecipientEntitiesInput
) {
  return filterPossibleRecipientEntities(input).map((e) => ({
    display: e.subtype.displayName,
    value: e.id,
  }));
}

interface UseRecipientOptionsParamsOptions {
  /** When provided, overrides the disposition scheme check for determining
   *  whether to omit the surviving spouse option. In the V2 form, both death
   *  sections render simultaneously so the global dispositionScheme field
   *  cannot distinguish between sections. */
  deathEvent?: PrimaryClientDeathEvent;
}

export function useRecipientOptionsParams(
  options?: UseRecipientOptionsParamsOptions
): UseRecipientOptionsParams {
  const { allClientIds, isTestamentaryEntity } =
    useDispositiveProvisionsContext();
  const dispositionScheme = useDispositionScheme();
  const dyingPrimaryClientId = useFirstClientDeathId();

  const deadPrimaryClientIds = useMemo(() => {
    if (isTestamentaryEntity) return allClientIds;
    if (dispositionScheme !== DispositionScheme.UPON_SECOND_DEATH) {
      return compact([dyingPrimaryClientId]);
    }
    return allClientIds;
  }, [
    isTestamentaryEntity,
    allClientIds,
    dispositionScheme,
    dyingPrimaryClientId,
  ]);

  // After the 2nd death, there is no surviving spouse, so omit that option.
  // In the V2 form, both death sections render simultaneously, so use the
  // deathEvent prop (which is section-specific) when available instead of
  // the global dispositionScheme field.
  const omitSurvivingSpouseOption = options?.deathEvent
    ? options.deathEvent === PrimaryClientDeathEvent.SECOND_DEATH
    : dispositionScheme === DispositionScheme.UPON_SECOND_DEATH;

  return {
    deadPrimaryClientIds,
    omitSurvivingSpouseOption,
  };
}

function sortDisabledToEnd(
  a: { disabled: boolean; display: string },
  b: { disabled: boolean; display: string }
) {
  if (a.disabled && !b.disabled) return 1;
  if (!a.disabled && b.disabled) return -1;
  return a.display.localeCompare(b.display);
}

interface RecipientTypeaheadGroup {
  groupName: string;
  options: SelectInputOption<string>[];
}

/**
 * Flattens grouped recipient options into the shape the typeahead recipient
 * dropdown expects: a single list where each option carries a `groupName`
 * (consumed via the typeahead's `groupBy`) instead of the interspersed
 * group-label rows the MUI Select uses. `leadingUngrouped` (e.g. Surviving
 * Spouse) is placed first with no group so it renders without a section header.
 * Empty groups contribute nothing. Shared by the DP and BDG recipient forms so
 * their grouping stays aligned.
 */
export function buildRecipientTypeaheadOptions(
  groups: RecipientTypeaheadGroup[],
  leadingUngrouped?: SelectInputOption<string> | null
): TypeaheadSelectInputOption<string>[] {
  const grouped = groups.flatMap((group) =>
    group.options.map(
      (option): TypeaheadSelectInputOption<string> => ({
        ...option,
        groupName: group.groupName,
      })
    )
  );
  return leadingUngrouped ? [leadingUngrouped, ...grouped] : grouped;
}

export interface UseRecipientOptionsParams {
  deadPrimaryClientIds: string[];
  /** Omits the surviving spouse option for non-template usages */
  omitSurvivingSpouseOption?: boolean;
}

export function useRecipientOptions(
  {
    deadPrimaryClientIds,
    omitSurvivingSpouseOption,
  }: UseRecipientOptionsParams,
  recipientConfiguration: DispositiveProvisionsRecipientTemplateRecipientConfiguration = DispositiveProvisionsTemplateRules
    .NO_TEMPLATE.recipientConfiguration
) {
  const { householdId } = useHouseholdDetailsContext();
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();

  const sharedRecipients = useRecipientData();
  const { data: clientData } = useDispositiveProvisionsPossibleRecipientsQuery({
    skip: sharedRecipients !== undefined,
    fetchPolicy: 'cache-and-network',
    variables: {
      where: { id: householdId },
      // the hasDeadGrantors var is used to include testamentary entities in the query
      // response; since templates can always have testamentary entities as recipients,
      // and because they aren't associated with a specific death, they should always be
      // included in the query response
      hasDeadGrantors: deadPrimaryClientIds.length > 0 || isTemplateMode,
    },
  });

  const household = getNodes(
    sharedRecipients
      ? sharedRecipients.data?.households
      : clientData?.households
  )[0];

  return useRecipientOptionsFromHousehold(
    household,
    { deadPrimaryClientIds, omitSurvivingSpouseOption },
    recipientConfiguration
  );
}

// Both the regular dropdown and the matcher use the same eligibility rules.
// The matcher supplies its combined query result instead of fetching it again.
export function useRecipientOptionsFromHousehold(
  household:
    | DispositiveProvisionsPossibleRecipients_HouseholdFragment
    | undefined,
  {
    deadPrimaryClientIds,
    omitSurvivingSpouseOption,
  }: UseRecipientOptionsParams,
  recipientConfiguration: DispositiveProvisionsRecipientTemplateRecipientConfiguration = DispositiveProvisionsTemplateRules
    .NO_TEMPLATE.recipientConfiguration
) {
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const { entityId } = useEntityDetailsContext();
  const { isOnHypotheticalWaterfall } = useDispositiveProvisionsContext();
  const { isTemplateMode } =
    useDispositiveProvisionsTemplateSplitScreenModalContext();

  return useMemo(() => {
    if (!household) {
      return {
        clientProfileRecipients: [],
        entityRecipients: [],
        organizationRecipients: [],
        testamentaryEntityRecipients: [],
        options: [],
        typeaheadOptions: [],
      };
    }

    const {
      allowsIndividualRecipient: templateAllowsIndividuals,
      allowsOrganizationRecipient: templateAllowsOrganizations,
      allowsEntityRecipient: templateAllowsEntities,
      allowsTestamentaryEntityRecipient: templateAllowsTestamentaryEntities,
      allowsToSurvivingSpouse: templateAllowsSurvivingSpouse,
      customIndividualFilter,
      customEntityFilter,
    } = recipientConfiguration;

    // CLIENT PROFILES
    const possibleClientProfiles = household.clientProfiles ?? [];
    const clientProfileOptions = templateAllowsIndividuals
      ? possibleClientProfiles
          .filter(customIndividualFilter ? customIndividualFilter : () => true)
          .map((c) => {
            const hasDeceased = deadPrimaryClientIds.includes(c.id);
            return {
              display: compact([
                c.displayName,
                hasDeceased && '(deceased)',
              ]).join(' '),
              value: c.id,
              disabled: hasDeceased,
            };
          })
          .sort(sortDisabledToEnd)
      : [];

    // TESTAMENTARY ENTITIES
    const testamentaryEntityRecipients = templateAllowsTestamentaryEntities
      ? getNodes(household.testamentaryEntities)
      : [];
    const testamentaryEntityRecipientOptions = testamentaryEntityRecipients.map(
      (te) => {
        return {
          display: te.displayName,
          value: te.id,
          secondaryText:
            formatCreatedFromEntities(te.createdFromEntities) ?? undefined,
        };
      }
    );

    // ORGANIZATIONS
    const organizationRecipients = templateAllowsOrganizations
      ? (household.clientOrganizations ?? [])
      : [];
    const organizationRecipientOptions = organizationRecipients
      .map((c) => ({
        display: c.name,
        value: c.id,
      }))
      .sort((a, b) => a.display.localeCompare(b.display));

    const entityRecipients = getNodes(household.entities);
    const entityRecipientOptions = templateAllowsEntities
      ? getEntityRecipientOptions({
          entities: entityRecipients,
          includeStages: [
            EntityStage.Active,
            EntityStage.Completed,
            EntityStage.PreCreated,
          ],
          excludeEntities: entityId ? [entityId] : undefined,
          customEntityFilter,
        }).sort((a, b) => a.display.localeCompare(b.display))
      : [];

    const draftEntityRecipientOptions = (() => {
      if (!isOnHypotheticalWaterfall) {
        return [];
      }

      return templateAllowsEntities
        ? getEntityRecipientOptions({
            entities: entityRecipients,
            includeStages: [EntityStage.Draft],
            excludeEntities: entityId ? [entityId] : undefined,
            customEntityFilter,
          }).sort((a, b) => a.display.localeCompare(b.display))
        : [];
    })();

    const allowSurvivingSpouseOption = (() => {
      if (!isTwoClientHousehold) {
        return false;
      }
      if (isTemplateMode) {
        return !!templateAllowsSurvivingSpouse && !!templateAllowsIndividuals;
      }
      return !omitSurvivingSpouseOption;
    })();

    const options = compact([
      (() =>
        allowSurvivingSpouseOption
          ? {
              display: 'Surviving Spouse',
              startAdornment: (
                <ListItemIcon>
                  <User01Icon size={16} />
                </ListItemIcon>
              ),
              value: SURVIVING_SPOUSE_SENTINEL,
            }
          : null)(),
      (() =>
        clientProfileOptions.length
          ? {
              component: <SelectItemGroupLabel label="Individuals" />,
              type: 'component' as const,
            }
          : null)(),
      ...clientProfileOptions,
      (() =>
        organizationRecipientOptions.length
          ? {
              component: <SelectItemGroupLabel label="Organizations" />,
              type: 'component' as const,
            }
          : null)(),
      ...organizationRecipientOptions,
      (() =>
        testamentaryEntityRecipientOptions.length
          ? {
              component: <SelectItemGroupLabel label="Sub-trusts" />,
              type: 'component' as const,
            }
          : null)(),
      ...testamentaryEntityRecipientOptions,
      (() =>
        entityRecipientOptions.length
          ? {
              component: <SelectItemGroupLabel label="Entities" />,
              type: 'component' as const,
            }
          : null)(),

      ...entityRecipientOptions,
      (() =>
        draftEntityRecipientOptions.length
          ? {
              component: <SelectItemGroupLabel label="Draft Entities" />,
              type: 'component' as const,
            }
          : null)(),

      ...draftEntityRecipientOptions,
    ]);

    // Flat, grouped-by-`groupName` variant for the typeahead recipient dropdown.
    // Mirrors the section order of `options` above. Surviving Spouse leads
    // without a group so it renders header-less.
    const typeaheadOptions = buildRecipientTypeaheadOptions(
      [
        { groupName: 'Individuals', options: clientProfileOptions },
        { groupName: 'Organizations', options: organizationRecipientOptions },
        {
          groupName: 'Sub-trusts',
          options: testamentaryEntityRecipientOptions,
        },
        { groupName: 'Entities', options: entityRecipientOptions },
        { groupName: 'Draft Entities', options: draftEntityRecipientOptions },
      ],
      allowSurvivingSpouseOption
        ? {
            display: 'Surviving Spouse',
            value: SURVIVING_SPOUSE_SENTINEL,
            startAdornment: (
              <ListItemIcon>
                <User01Icon size={16} />
              </ListItemIcon>
            ),
          }
        : null
    );

    return {
      options,
      typeaheadOptions,
      entityRecipients,
      testamentaryEntityRecipients,
      organizationRecipients,
      clientProfileRecipients: possibleClientProfiles,
    };
  }, [
    household,
    recipientConfiguration,
    entityId,
    deadPrimaryClientIds,
    isOnHypotheticalWaterfall,
    isTwoClientHousehold,
    isTemplateMode,
    omitSurvivingSpouseOption,
  ]);
}
